import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Megaphone, MapPin, Activity, CheckCircle2, ExternalLink, Building2, Users, Landmark, Mail } from "lucide-react";

const STEPS = [
  { icon: Megaphone, title: "Report It", text: "Residents log a municipal problem — potholes, drainage, lighting, bylaw concerns — in minutes." },
  { icon: MapPin, title: "Map It", text: "Public-safe issues appear on a community map so patterns become visible to everyone." },
  { icon: Activity, title: "Track It", text: "Each issue gets a status trail: reported, acknowledged, in progress, resolved." },
  { icon: CheckCircle2, title: "Get Answers", text: "Follow-up is documented, so residents can see what happened — and what didn't." },
];

export default function Pita() {
  return (
    <div>
      <section className="bg-white border-b border-slate-200">
        <div className="wyv-container py-14">
          <div className="flex flex-col sm:flex-row items-start gap-6 max-w-3xl">
            <img
              src="/pita-logo.png"
              alt="PITA — Public Issue Tracker App logo"
              className="h-20 w-auto rounded-lg border border-slate-200 bg-white p-1.5 shrink-0"
            />
            <div>
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 mb-3">
                A related municipal accountability tool
              </Badge>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground leading-tight" data-testid="pita-title">
                PITA — a municipal issue tracker.
              </h1>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                PITA (Put It In PITA) is a related municipal issue-tracking tool, currently associated with the
                City of Kawartha Lakes demonstration on stevelesperance.com. Residents report local problems,
                public-safe issues are mapped, and follow-up is tracked. It could potentially be made available to
                other Ontario municipalities.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild data-testid="pita-request-button">
              <Link to="/pita/request">Request PITA for My Municipality</Link>
            </Button>
            <Button asChild variant="outline" data-testid="pita-contact-button">
              <Link to="/contact"><Mail className="h-4 w-4 mr-1.5" /> Contact Steve About PITA</Link>
            </Button>
            <a
              href="https://stevelesperance.com/map"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pita-demo-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground underline underline-offset-4 px-2 py-2"
            >
              See the external demonstration <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 max-w-xl">
            The demonstration on stevelesperance.com is an external demonstration site. It is not part of the Witness
            Your Vote declaration system and shares no data with it.
          </p>
        </div>
      </section>

      <section className="wyv-container py-14">
        <h2 className="font-heading font-bold text-2xl text-foreground mb-8">How PITA works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s) => (
            <Card key={s.title} className="h-full">
              <CardContent className="p-6">
                <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-2">{s.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="wyv-container py-14">
          <h2 className="font-heading font-bold text-2xl text-foreground mb-3">Interested in PITA for your municipality?</h2>
          <p className="text-muted-foreground max-w-2xl mb-8">
            PITA is currently demonstrated for the City of Kawartha Lakes. If you would like PITA made available for
            your municipality beyond Kawartha Lakes, get in touch — residents, municipalities, candidates and
            taxpayer groups are all welcome to ask.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, t: "Residents", d: "Bring visible issue-tracking to your neighbourhood." },
              { icon: Landmark, t: "Municipalities", d: "Give residents a transparent follow-up channel." },
              { icon: Megaphone, t: "Candidates", d: "Show voters you take local problems seriously." },
              { icon: Building2, t: "Taxpayer groups", d: "Document patterns that deserve answers." },
            ].map((x) => (
              <Card key={x.t}><CardContent className="p-5">
                <x.icon className="h-5 w-5 text-accent mb-3" />
                <h3 className="font-heading font-semibold text-sm mb-1">{x.t}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{x.d}</p>
              </CardContent></Card>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="pita-request-bottom"><Link to="/pita/request">Request PITA</Link></Button>
            <Button asChild variant="outline"><Link to="/contact">Contact Steve About PITA</Link></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-6 max-w-2xl">
            PITA is a supporting service. Witness Your Vote remains the main platform — an independent,
            non-partisan election-transparency initiative.
          </p>
        </div>
      </section>
    </div>
  );
}
